import React, {startTransition, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {QueryKey, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useSearchParams} from 'react-router-dom';
import {BrowsePageFrame, BrowseResults, BrowseResultState} from '@/components/football';
import PageHeader from '@/components/layout/PageHeader';
import PlayersFilters from '@/components/players/PlayersFilters';
import Pagination from '@/components/ui/Pagination';
import Button from '@/components/ui/Button';
import EmptyState from '@/components/ui/EmptyState';
import {useWorkspace} from '@/context/WorkspaceContext';
import {PlayerBrowseItem, PlayerBrowseResponse, PlayerScope, playersV2API} from '@/features/players/api';
import {playerBrowseDefaults, playerBrowseCodec, scoutingPlayerBrowseCodec, scoutingPlayerBrowseDefaults} from '@/features/players/playerUrlState';
import {playerBrowseQuery, playerKeys} from '@/features/players/queries';
import {PlayerBrowseState} from '@/features/players/types';
import {usePlayerRealtime} from '@/features/players/usePlayerRealtime';
import {updateWatchlistBrowseData} from '@/features/players/watchlistCache';
import {getV2Error} from '@/services/api/v2';
import PlayerBrowseTable, {SquadLifecycleStatus} from './PlayerBrowseTable';
import PlayersGrid from './PlayersPage/PlayersGrid';
import ScoutingWyscoutSuggestions from './PlayersPage/ScoutingWyscoutSuggestions';
import SquadSyncPanel, {SquadReviewOpenRequest, syncResultLabel} from './PlayersPage/SquadSyncPanel';

interface PlayersPageProps {
  mode?: 'squad' | 'scouting';
}

interface RetainedPlayerData {
  workspaceId: string;
  scope: PlayerScope;
  data: PlayerBrowseResponse;
}

interface WatchVariables {
  workspaceId: string;
  playerId: string;
  playerName: string;
  isWatched: boolean;
}

interface WatchMutationContext {
  snapshots: Array<[QueryKey, PlayerBrowseResponse | undefined]>;
}

type WatchBatchSnapshots = Map<string, [QueryKey, PlayerBrowseResponse | undefined]>;

const hasActiveFilters = (state: PlayerBrowseState): boolean =>
  Boolean(state.q || state.position || state.team || state.availability
    || state.minAge !== undefined || state.maxAge !== undefined
    || state.minRating !== undefined || state.maxRating !== undefined
    || state.recentDays !== undefined || state.playedLastMatch || state.squadStatus === 'archived');

const PlayersPage: React.FC<PlayersPageProps> = ({mode = 'squad'}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {currentWorkspace} = useWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';
  const queryClient = useQueryClient();
  const scope: PlayerScope = mode === 'squad' ? 'squad' : 'external';
  const realtime = usePlayerRealtime(currentWorkspace?.id ?? '');
  const root = mode === 'squad' ? '/players' : '/scouting';
  const title = mode === 'squad' ? 'Squad' : 'Scouting';
  const browseCodec = mode === 'scouting' ? scoutingPlayerBrowseCodec : playerBrowseCodec;
  const browseDefaults = mode === 'scouting' ? scoutingPlayerBrowseDefaults : playerBrowseDefaults;
  const reviewRequested = mode === 'squad' && searchParams.get('review') === 'wyscout';
  const browseSearchParams = new URLSearchParams(searchParams);
  browseSearchParams.delete('review');
  const state = browseCodec.parse(browseSearchParams);
  if (mode !== 'squad') state.squadStatus = 'current';
  const squadStatus = state.squadStatus ?? 'current';
  const rawSearch = searchParams.toString();
  const canonicalBrowseParams = browseCodec.serialize(state);
  const canonicalBrowseSearch = canonicalBrowseParams.toString();
  const canonicalParams = new URLSearchParams(canonicalBrowseParams);
  if (reviewRequested) canonicalParams.set('review', 'wyscout');
  const canonicalSearch = canonicalParams.toString();
  const [searchValue, setSearchValue] = useState(state.q);
  const urlStateRef = useRef(canonicalBrowseSearch);
  const searchPendingRef = useRef(false);
  const retainedDataRef = useRef<RetainedPlayerData | undefined>(undefined);
  const activeWorkspaceRef = useRef(workspaceId);
  activeWorkspaceRef.current = workspaceId;
  const [syncFeedback, setSyncFeedback] = useState<{type: 'pending' | 'success' | 'error'; message: string} | undefined>();
  const [latestWorkflowOperation, setLatestWorkflowOperation] = useState<'sync' | 'decision'>();
  const reviewOpenRequestIdRef = useRef(reviewRequested && workspaceId ? 1 : 0);
  const reviewDeepLinkOriginWorkspaceRef = useRef<string | undefined>(reviewRequested && workspaceId ? workspaceId : undefined);
  const [reviewOpenRequest, setReviewOpenRequest] = useState<{
    workspaceId: string;
    request: SquadReviewOpenRequest;
  } | undefined>(() => reviewRequested && workspaceId ? {
    workspaceId,
    request: {id: 1, afterDataUpdatedAt: 0},
  } : undefined);
  const [syncingWorkspaceIds, setSyncingWorkspaceIds] = useState<ReadonlySet<string>>(() => new Set());
  const [watchingPendingKeys, setWatchingPendingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [watchFailures, setWatchFailures] = useState<ReadonlyMap<string, {variables: WatchVariables; message: string}>>(() => new Map());
  const activeWatchMutationsRef = useRef(new Map<string, WatchVariables>());
  const successfulWatchMutationsRef = useRef(new Map<string, WatchVariables>());
  const watchBatchSnapshotsRef = useRef(new Map<string, WatchBatchSnapshots>());
  const query = useQuery({
    ...playerBrowseQuery(workspaceId, scope, state),
    enabled: Boolean(workspaceId),
  });
  if (query.data && !query.isPlaceholderData) {
    retainedDataRef.current = {workspaceId, scope, data: query.data};
  }
  const retainedData = retainedDataRef.current;
  const retainedDataMatchesContext = retainedData?.workspaceId === workspaceId && retainedData.scope === scope;
  const capabilityData = retainedDataMatchesContext ? retainedData.data : undefined;
  const canManageSquad = mode === 'squad' && Boolean(capabilityData?.capabilities.includes('wyscout.squad.sync'));
  const canManageWatchlist = mode === 'scouting'
    && Boolean(capabilityData?.capabilities.includes('scouting.watchlist.manage'));
  const sync = useMutation({
    mutationFn: (requestedWorkspaceId: string) => playersV2API.syncSquad(requestedWorkspaceId),
    onMutate: requestedWorkspaceId => {
      setSyncingWorkspaceIds(current => {
        const next = new Set(current);
        next.add(requestedWorkspaceId);
        return next;
      });
      if (activeWorkspaceRef.current === requestedWorkspaceId) {
        setLatestWorkflowOperation('sync');
        setSyncFeedback({type: 'pending', message: 'Synchronizing Wyscout squad...'});
      }
    },
    onSuccess: async (result, requestedWorkspaceId) => {
      await queryClient.cancelQueries({
        queryKey: playerKeys.squadReview(requestedWorkspaceId),
        exact: true,
      });
      const reviewDataUpdatedAt = queryClient
        .getQueryState(playerKeys.squadReview(requestedWorkspaceId))?.dataUpdatedAt ?? 0;
      void Promise.all([
        queryClient.invalidateQueries({queryKey: playerKeys.all(requestedWorkspaceId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.latestSquadSync(requestedWorkspaceId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.squadReview(requestedWorkspaceId)}),
      ]);
      if (activeWorkspaceRef.current === requestedWorkspaceId) {
        setSyncFeedback({type: 'success', message: `Sync complete: ${syncResultLabel(result)}`});
        if (result.review > 0) {
          reviewOpenRequestIdRef.current += 1;
          setReviewOpenRequest({
            workspaceId: requestedWorkspaceId,
            request: {id: reviewOpenRequestIdRef.current, afterDataUpdatedAt: reviewDataUpdatedAt},
          });
        }
      }
    },
    onError: (error, requestedWorkspaceId) => {
      if (activeWorkspaceRef.current === requestedWorkspaceId) {
        setSyncFeedback({type: 'error', message: getV2Error(error).message});
      }
    },
    onSettled: (_data, _error, requestedWorkspaceId) => {
      setSyncingWorkspaceIds(current => {
        const next = new Set(current);
        next.delete(requestedWorkspaceId);
        return next;
      });
    },
  });
  const watchMutation = useMutation<unknown, unknown, WatchVariables, WatchMutationContext>({
    mutationFn: variables => playersV2API.setWatched(variables.playerId, variables.isWatched, variables.workspaceId),
    onMutate: async variables => {
      await queryClient.cancelQueries({queryKey: playerKeys.all(variables.workspaceId)});
      const snapshots = queryClient
        .getQueriesData<PlayerBrowseResponse>({queryKey: playerKeys.all(variables.workspaceId)})
        .filter(([key, value]) => key[3] === 'external' && typeof key[4] === 'object' && value !== undefined);
      const baseline = watchBatchSnapshotsRef.current.get(variables.workspaceId) ?? new Map();
      snapshots.forEach(snapshot => {
        const [key] = snapshot;
        const snapshotKey = JSON.stringify(key);
        if (!baseline.has(snapshotKey)) baseline.set(snapshotKey, snapshot);
      });
      watchBatchSnapshotsRef.current.set(variables.workspaceId, baseline);
      snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(
          key,
          updateWatchlistBrowseData(data, key[4] as PlayerBrowseState, variables.playerId, variables.isWatched),
        );
      });
      return {snapshots};
    },
    onSuccess: (_data, variables) => {
      successfulWatchMutationsRef.current.set(`${variables.workspaceId}:${variables.playerId}`, variables);
      setWatchFailures(current => {
        const next = new Map(current);
        next.delete(`${variables.workspaceId}:${variables.playerId}`);
        return next;
      });
    },
    onError: (error, variables, context) => {
      const baseline = watchBatchSnapshotsRef.current.get(variables.workspaceId);
      const snapshots = baseline ? Array.from(baseline.values()) : context?.snapshots ?? [];
      snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
      const failedKey = `${variables.workspaceId}:${variables.playerId}`;
      const replay = new Map<string, WatchVariables>();
      successfulWatchMutationsRef.current.forEach((pending, key) => replay.set(key, pending));
      activeWatchMutationsRef.current.forEach((pending, key) => replay.set(key, pending));
      replay.forEach((pending, pendingKey) => {
        if (pending.workspaceId === variables.workspaceId && pendingKey !== failedKey) {
          snapshots.forEach(([key]) => {
            queryClient.setQueryData<PlayerBrowseResponse | undefined>(key, (data: PlayerBrowseResponse | undefined) => (
              updateWatchlistBrowseData(data, key[4] as PlayerBrowseState, pending.playerId, pending.isWatched)
            ));
          });
        }
      });
      if (activeWorkspaceRef.current === variables.workspaceId) {
        setWatchFailures(current => new Map(current).set(failedKey, {variables, message: getV2Error(error).message}));
      }
    },
    onSettled: (_data, _error, variables) => {
      const pendingKey = `${variables.workspaceId}:${variables.playerId}`;
      activeWatchMutationsRef.current.delete(pendingKey);
      setWatchingPendingKeys(current => {
        const next = new Set(current);
        next.delete(pendingKey);
        return next;
      });
      const workspaceStillPending = Array.from(activeWatchMutationsRef.current.values())
        .some(pending => pending.workspaceId === variables.workspaceId);
      void queryClient.invalidateQueries({
        queryKey: playerKeys.all(variables.workspaceId),
        refetchType: workspaceStillPending ? 'none' : 'active',
      });
      if (!workspaceStillPending) {
        watchBatchSnapshotsRef.current.delete(variables.workspaceId);
        successfulWatchMutationsRef.current.forEach((pending, key) => {
          if (pending.workspaceId === variables.workspaceId) successfulWatchMutationsRef.current.delete(key);
        });
      }
    },
  });
  useEffect(() => {
    setSyncFeedback(undefined);
    setLatestWorkflowOperation(undefined);
    if (!reviewRequested) {
      reviewDeepLinkOriginWorkspaceRef.current = undefined;
      setReviewOpenRequest(undefined);
      return;
    }
    if (!workspaceId) {
      setReviewOpenRequest(undefined);
      return;
    }
    if (reviewDeepLinkOriginWorkspaceRef.current === undefined) {
      reviewDeepLinkOriginWorkspaceRef.current = workspaceId;
      reviewOpenRequestIdRef.current += 1;
      setReviewOpenRequest({
        workspaceId,
        request: {id: reviewOpenRequestIdRef.current, afterDataUpdatedAt: 0},
      });
    } else if (reviewDeepLinkOriginWorkspaceRef.current !== workspaceId) {
      setReviewOpenRequest(undefined);
    }
  }, [reviewRequested, workspaceId]);

  useEffect(() => {
    if (rawSearch !== canonicalSearch) setSearchParams(canonicalSearch, {replace: true});
  }, [canonicalSearch, rawSearch, setSearchParams]);

  useLayoutEffect(() => {
    if (urlStateRef.current !== canonicalBrowseSearch) {
      urlStateRef.current = canonicalBrowseSearch;
      if (!searchPendingRef.current) setSearchValue(state.q);
    }
  }, [canonicalBrowseSearch, state.q]);

  useEffect(() => {
    if (searchValue === state.q) {
      searchPendingRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      searchPendingRef.current = false;
      startTransition(() => {
        const params = browseCodec.serialize({...state, q: searchValue, page: 1});
        if (reviewRequested) params.set('review', 'wyscout');
        setSearchParams(params, {replace: true});
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [browseCodec, reviewRequested, searchValue, setSearchParams, state]);

  const commitState = (change: Partial<PlayerBrowseState>) => {
    const params = browseCodec.serialize({...state, q: state.q, ...change, page: 1});
    if (reviewRequested) params.set('review', 'wyscout');
    setSearchParams(params);
  };
  const changeSearch = (value: string) => {
    searchPendingRef.current = value !== state.q;
    setSearchValue(value);
  };
  const clearFilters = () => {
    searchPendingRef.current = false;
    setSearchValue('');
    const params = browseCodec.serialize(mode === 'scouting'
      ? {...browseDefaults, watching: state.watching}
      : browseDefaults);
    if (reviewRequested) params.set('review', 'wyscout');
    setSearchParams(params);
  };
  const returnTo = `${root}${canonicalBrowseSearch ? `?${canonicalBrowseSearch}` : ''}`;
  const withReturnTo = (path: string): string => `${path}?${new URLSearchParams({from: returnTo})}`;
  const changeWyscoutSearch = () => {
    const search = document.getElementById('player-search');
    if (!(search instanceof HTMLInputElement)) return;
    search.scrollIntoView({block: 'center', inline: 'nearest'});
    search.focus();
    search.select();
  };
  const detailHref = (player: PlayerBrowseItem): string => withReturnTo(`${root}/${player.id}`);
  const queryData = query.isPlaceholderData && !retainedDataMatchesContext ? undefined : query.data;
  const data = queryData ?? (query.isError && retainedDataMatchesContext ? retainedData.data : undefined);
  const activeWorkspaceWatchPending = Array.from(activeWatchMutationsRef.current.values())
    .some(pending => pending.workspaceId === workspaceId);
  const correctedPage = activeWorkspaceWatchPending || !data || query.isPlaceholderData
    ? undefined
    : data.meta.total === 0
      ? (state.page === 1 ? undefined : 1)
      : state.page > data.meta.totalPages
        ? Math.max(1, data.meta.totalPages)
        : undefined;
  const canCreate = data?.capabilities.includes('player.create');
  const trimmedCommittedSearch = state.q.trim();
  const localNameSettled = searchValue === state.q;
  const wyscoutFallbackContext = mode === 'scouting'
    && !state.watching
    && Boolean(workspaceId && canCreate)
    && Array.from(trimmedCommittedSearch).length >= 3;
  const wyscoutFallbackEligible = wyscoutFallbackContext
    && localNameSettled
    && correctedPage === undefined
    && !query.isPending
    && !query.isFetching
    && !query.isError
    && !query.isPlaceholderData
    && data?.meta.total === 0;
  const squadStatusById = new Map<string, SquadLifecycleStatus>();
  if (mode === 'squad' && data) {
    data.data.forEach((player: PlayerBrowseItem) => {
      if (player.squadStatus === 'review' || player.squadStatus === 'archived') squadStatusById.set(player.id, player.squadStatus);
    });
  }

  useEffect(() => {
    if (correctedPage !== undefined) {
      const params = browseCodec.serialize({...state, page: correctedPage});
      if (reviewRequested) params.set('review', 'wyscout');
      setSearchParams(params, {replace: true});
    }
  }, [browseCodec, correctedPage, reviewRequested, setSearchParams, state]);

  const watchPending = new Set<string>();
  watchingPendingKeys.forEach(key => {
    const prefix = `${workspaceId}:`;
    if (key.startsWith(prefix)) watchPending.add(key.slice(prefix.length));
  });
  const changeWatch = (player: PlayerBrowseItem, isWatched: boolean) => {
    const variables = {workspaceId, playerId: player.id, playerName: player.name, isWatched};
    submitWatch(variables, true);
  };
  const submitWatch = (variables: WatchVariables, clearFailure: boolean) => {
    const pendingKey = `${variables.workspaceId}:${variables.playerId}`;
    if (!variables.workspaceId || activeWatchMutationsRef.current.has(pendingKey)) return;
    activeWatchMutationsRef.current.set(pendingKey, variables);
    setWatchingPendingKeys(current => new Set(current).add(pendingKey));
    if (clearFailure && activeWorkspaceRef.current === variables.workspaceId) {
      setWatchFailures(current => {
        const next = new Map(current);
        next.delete(pendingKey);
        return next;
      });
    }
    watchMutation.mutate(variables);
  };
  const watchedEmpty = mode === 'scouting' && state.watching && correctedPage === undefined
    && !query.isPlaceholderData
    && data?.meta.total === 0;
  const visibleWatchFailures = Array.from(watchFailures.entries())
    .filter(([, failure]) => failure.variables.workspaceId === workspaceId)
    .sort(([left], [right]) => left.localeCompare(right));

  let resultState: BrowseResultState;
  if (correctedPage !== undefined) {
    resultState = {status: 'loading', message: 'Loading a valid player page...'};
  } else if (query.isPending && !data) {
    resultState = {status: 'loading', message: `Loading ${title.toLowerCase()} players...`};
  } else if (query.isError && !data) {
    resultState = {status: 'error', message: `Unable to load ${title.toLowerCase()} players.`, retry: () => { void query.refetch(); }};
  } else if (query.isPlaceholderData && query.isFetching && data?.data.length === 0) {
    resultState = {status: 'loading', message: 'Loading the restored player page...'};
  } else if (!data || data.meta.total === 0) {
    resultState = hasActiveFilters(state)
      ? {status: 'filtered-empty', message: 'No players match these filters.', clearFilters}
      : {status: 'empty', message: mode === 'squad' ? 'No squad players have been added.' : 'No scouting players have been added.'};
  } else if (data.data.length === 0) {
    resultState = {status: 'error', message: 'Unable to display this player page.', retry: () => { void query.refetch(); }};
  } else {
    resultState = {
      status: 'success',
      refreshing: query.isFetching,
      table: <PlayerBrowseTable players={data.data} label={`${title} players`} detailHref={detailHref} squadStatusById={squadStatusById} canManageWatchlist={canManageWatchlist} watchPending={watchPending} onWatchChange={changeWatch} />,
      cards: <PlayersGrid players={data.data} detailHref={detailHref} squadStatusById={squadStatusById} canManageWatchlist={canManageWatchlist} watchPending={watchPending} onWatchChange={changeWatch} />,
    };
  }

  const total = data?.meta.total ?? 0;
  const summary = query.isPending && !data
    ? 'Loading player count'
    : wyscoutFallbackEligible
      ? '0 local players matching filters'
      : `${total} ${total === 1 ? 'player' : 'players'}${hasActiveFilters(state) ? ' matching filters' : ''}`;
  const actionClass = 'inline-flex min-h-11 items-center rounded-md px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500';
  const playerActions = canCreate ? (
    <>
      {mode === 'scouting' && <Link className={`${actionClass} border border-gray-300 text-club-blue-700 hover:bg-gray-50 dark:border-gray-600 dark:text-club-blue-300 dark:hover:bg-gray-800`} to={withReturnTo('/scouting/import')}>Import from WyScout</Link>}
      <Link className={`${actionClass} bg-club-blue-700 text-white hover:bg-club-blue-800`} to={withReturnTo(`${root}/new`)}>Add player</Link>
    </>
  ) : undefined;
  const currentWorkspaceSyncPending = syncingWorkspaceIds.has(workspaceId);
  const syncAction = canManageSquad ? (
    <Button className="min-h-11" variant="outline" loading={currentWorkspaceSyncPending} loadingLabel="Synchronizing Wyscout squad" onClick={() => { if (!currentWorkspaceSyncPending) sync.mutate(workspaceId); }}>Sync Wyscout squad</Button>
  ) : undefined;

  return (
    <BrowsePageFrame
      header={<PageHeader title={title} actions={playerActions || syncAction ? <>{playerActions}{syncAction}</> : undefined} />}
      toolbar={(
        <div className="space-y-4">
          {mode === 'scouting' && (
            <div className="flex flex-wrap gap-2 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800" role="group" aria-label="Scouting player view">
              <Button size="sm" variant={state.watching ? 'primary' : 'outline'} aria-pressed={state.watching} onClick={() => commitState({watching: true, page: 1})}>Watching</Button>
              <Button size="sm" variant={!state.watching ? 'primary' : 'outline'} aria-pressed={!state.watching} onClick={() => commitState({watching: false, page: 1})}>All players</Button>
            </div>
          )}
          {mode === 'squad' && !canManageSquad && (
            <div className="flex flex-wrap gap-2 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800" role="group" aria-label="Squad lifecycle">
              <Button size="sm" variant={squadStatus === 'current' ? 'primary' : 'outline'} aria-pressed={squadStatus === 'current'} onClick={() => commitState({squadStatus: 'current'})}>Current players</Button>
              <Button size="sm" variant={squadStatus === 'archived' ? 'primary' : 'outline'} aria-pressed={squadStatus === 'archived'} onClick={() => commitState({squadStatus: 'archived'})}>Archived players</Button>
            </div>
          )}
          {canManageSquad && currentWorkspace && (
            <SquadSyncPanel
              key={currentWorkspace.id}
              workspaceId={currentWorkspace.id}
              squadStatus={squadStatus}
              onSquadStatusChange={squadStatus => commitState({squadStatus})}
              syncFeedback={syncFeedback}
              latestOperation={latestWorkflowOperation}
              reviewOpenRequest={reviewOpenRequest?.workspaceId === workspaceId ? reviewOpenRequest.request : undefined}
              onReviewClose={() => setSearchParams(canonicalBrowseParams, {replace: true})}
              onDecisionStart={() => {
                setLatestWorkflowOperation('decision');
                setSyncFeedback(undefined);
              }}
            />
          )}
          <PlayersFilters state={state} searchValue={searchValue} facets={data?.facets} showTeam={mode === 'scouting'} onChange={commitState} onSearch={changeSearch} />
        </div>
      )}
      resultSummary={(
        <>
          {currentWorkspace && realtime.status === 'disconnected' && (
            <p role="status" aria-label="Live update status">Live updates disconnected. Results can still be refreshed.</p>
          )}
          {query.isError && data && (
            <div role="alert" className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-danger-300 bg-danger-50 p-3 text-danger-900">
              <span>Unable to refresh players: {getV2Error(query.error).message}</span>
              <button type="button" className="font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500" onClick={() => { void query.refetch(); }}>Retry</button>
            </div>
          )}
          {visibleWatchFailures.map(([failureKey, failure]) => (
            <div key={failureKey} role="alert" className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-danger-300 bg-danger-50 p-3 text-danger-900 dark:border-danger-700 dark:bg-danger-950 dark:text-danger-100">
              <span>{failure.message}</span>
              <button
                type="button"
                aria-label={`Retry watchlist update for ${failure.variables.playerName}`}
                className="font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500 disabled:cursor-wait disabled:opacity-60"
                disabled={watchPending.has(failure.variables.playerId)}
                onClick={() => submitWatch(failure.variables, false)}
              >Retry watchlist update</button>
            </div>
          ))}
          <p aria-live="polite">{summary}</p>
        </>
      )}
      results={watchedEmpty ? (
        <EmptyState
          title={hasActiveFilters(state) ? 'No watched players match these filters.' : 'No players are being watched yet.'}
          action={<Button variant="outline" className="min-h-11" onClick={() => commitState({watching: false, page: 1})}>View all players</Button>}
        />
      ) : wyscoutFallbackContext ? (
        <>
          <ScoutingWyscoutSuggestions
            workspaceId={workspaceId}
            nameQuery={trimmedCommittedSearch}
            returnPath={returnTo}
            active={wyscoutFallbackEligible}
            onChangeSearch={changeWyscoutSearch}
            manualCreateHref={withReturnTo(`${root}/new`)}
          />
          {!wyscoutFallbackEligible && <BrowseResults state={resultState} />}
        </>
      ) : <BrowseResults state={resultState} />}
      pagination={correctedPage === undefined && total > 0 ? <Pagination currentPage={state.page} totalCount={total} pageSize={state.pageSize} onPageChange={page => {
        const params = browseCodec.serialize({...state, page});
        if (reviewRequested) params.set('review', 'wyscout');
        setSearchParams(params);
      }} onPageSizeChange={pageSize => {
        const params = browseCodec.serialize({...state, pageSize: pageSize as PlayerBrowseState['pageSize'], page: 1});
        if (reviewRequested) params.set('review', 'wyscout');
        setSearchParams(params);
      }} isLoading={query.isFetching} /> : undefined}
    />
  );
};

export default PlayersPage;
